import { type ChangeEvent, type DragEvent, type MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import UploadFile from '@mui/icons-material/UploadFile';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { useTranslation } from 'react-i18next';
import { NotificationsContext } from 'components/notifications/NotificationsProvider';
import { SaveAssetDialogTitle } from '../../library/save-asset-dialog/SaveAssetDialogTitle';
import { useImportPdfIntoDocument } from '../hooks/useImportPdfIntoDocument';
import { getPdfPageCount, isInvalidPdfError, isPasswordProtectedError } from '../../../services/pdf/getPdfPageCount';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_PAGE_COUNT = 200;

interface ImportPdfDialogProps {
  afterSectionId?: string;
  isOpen: boolean;
  onClose: () => void;
  onImported: (sectionIds: string[]) => void;
}

export function ImportPdfDialog({ afterSectionId, isOpen, onClose, onImported }: ImportPdfDialogProps) {
  const { t } = useTranslation();
  const importPdfIntoDocument = useImportPdfIntoDocument();
  const { setNotifications } = useContext(NotificationsContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const validationRequestId = useRef(0);

  useEffect(() => {
    // Prevent async validation results from updating state after the dialog unmounts.
    return () => {
      validationRequestId.current += 1;
    };
  }, []);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const resetState = () => {
    setSelectedFile(null);
    setFileError(null);
    setHasUploadError(false);
    setIsDragging(false);
    setIsUploading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateAndSetFile = async (file: File) => {
    if (isUploading) return;

    const requestId = ++validationRequestId.current;
    const isStale = () => requestId !== validationRequestId.current;

    const isPdf = file.type === 'application/pdf' || (file.type === '' && file.name.toLowerCase().endsWith('.pdf'));
    if (!isPdf) {
      setSelectedFile(null);
      setFileError(t('editor.sections.pdf_upload_error_invalid_type'));
      setHasUploadError(true);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setFileError(t('editor.sections.pdf_upload_error_too_large'));
      setHasUploadError(true);
      return;
    }

    try {
      const pageCount = await getPdfPageCount(file);
      if (isStale()) return;
      if (pageCount > MAX_PAGE_COUNT) {
        setSelectedFile(null);
        setFileError(t('editor.sections.pdf_upload_error_too_many_pages'));
        setHasUploadError(true);
        return;
      }
    } catch (error) {
      if (isStale()) return;
      setSelectedFile(null);
      setHasUploadError(true);
      if (isPasswordProtectedError(error)) {
        setNotifications({ content: t('editor.sections.pdf_upload_error_password_protected'), severity: 'error' });
        setFileError(null);
      } else if (isInvalidPdfError(error)) {
        setNotifications({ content: t('editor.sections.pdf_upload_error_corrupted'), severity: 'error' });
        setFileError(null);
      } else {
        setFileError(t('editor.sections.pdf_upload_error_unreadable'));
      }
      return;
    }

    setFileError(null);
    setHasUploadError(false);
    setSelectedFile(file);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      validateAndSetFile(droppedFiles[0]);
    }
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
    event.target.value = '';
  };

  const handleUploadClick = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isUploading) return;
    setSelectedFile(null);
    setFileError(null);
    setHasUploadError(false);
  };

  const handleConvert = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setFileError(null);
    setHasUploadError(false);

    try {
      const addedSectionIds = await importPdfIntoDocument({ file: selectedFile, afterSectionId });

      if (addedSectionIds) {
        resetState();
        onClose();
        onImported(addedSectionIds);
      } else {
        setIsUploading(false);
        setFileError(t('editor.sections.pdf_upload_error_generic'));
        setHasUploadError(true);
      }
    } catch {
      setIsUploading(false);
      setFileError(t('editor.sections.pdf_upload_error_generic'));
      setHasUploadError(true);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <SaveAssetDialogTitle
        title={t('editor.sections.pdf_upload_dialog_title')}
        subtitle={t('editor.sections.pdf_upload_dialog_subtitle')}
        onClose={handleClose}
      />
      <DialogContent sx={{ pb: 0 }}>
        <Stack spacing={2} pb={3}>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            data-testid="pdf-file-input"
          />
          <Box
            data-testid="pdf-upload-area"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={(theme) => ({
              border: hasUploadError
                ? `1px solid ${theme.palette.error.main}`
                : isDragging
                ? `1px dashed ${theme.palette.secondary.main}`
                : '1px solid #E6EAEB',
              borderRadius: '24px',
              padding: '16px 24px',
              minHeight: 206,
              boxSizing: 'border-box',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: hasUploadError
                ? alpha(theme.palette.error.main, 0.08)
                : isDragging
                ? alpha(theme.palette.secondary.main, 0.08)
                : 'transparent',
              opacity: isUploading ? 0.6 : 1,
              pointerEvents: isUploading ? 'none' : 'auto',
              transition: theme.transitions.create(['background-color', 'border-color']),
            })}
          >
            <Stack spacing={1} alignItems="center">
              <UploadFile color="action" />
              <Typography component="div" variant="body1">
                <Typography
                  component="button"
                  type="button"
                  variant="body1"
                  color="secondary"
                  fontWeight={600}
                  onClick={handleUploadClick}
                  disabled={isUploading}
                  sx={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    margin: 0,
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    letterSpacing: 'inherit',
                    textAlign: 'inherit',
                    cursor: isUploading ? 'default' : 'pointer',
                  }}
                >
                  {selectedFile
                    ? t('editor.sections.pdf_upload_dialog_replace_label')
                    : t('editor.sections.pdf_upload_dialog_upload_label')}
                </Typography>{' '}
                <Typography component="span" variant="body1" color={isDragging ? 'text.primary' : 'text.secondary'}>
                  {t('editor.sections.pdf_upload_dialog_drop_label')}
                </Typography>
              </Typography>
              {selectedFile && (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{
                    border: '1px solid #E6EAEB',
                    borderRadius: '8px',
                    pl: 1.5,
                    pr: 0.5,
                    py: 0.5,
                    maxWidth: '100%',
                  }}
                >
                  <InsertDriveFileRounded color="primary" fontSize="small" />
                  <Typography variant="body2" color="primary" noWrap>
                    {selectedFile.name}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveFile} disabled={isUploading} aria-label="remove file">
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: fileError ? 0.5 : 3 }}>
            {t('editor.sections.pdf_upload_dialog_helper_text')}
          </Typography>
          {fileError && (
            <Typography variant="body2" color="error" sx={{ mb: 3 }}>
              {fileError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: '0rem 1.5rem 1.5rem 1.5rem', gap: '0.5rem', marginTop: '1.5rem' }}>
        <Button onClick={handleClose} color="primary" disabled={isUploading}>
          {t('button.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedFile || isUploading}
          onClick={handleConvert}
          startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isUploading ? t('editor.sections.pdf_upload_dialog_converting_button') : t('editor.sections.pdf_upload_dialog_convert_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
